import { ref, onMounted, watch } from "@vue/composition-api";

const TOOLTIP_SPACING = 5;
const TOOLTIP_DEFAULT_ANIMATION_DURATION = ".1s";
const TOOLTIP_NO_ANIMATION_DURATION = "0s";

function getFormattedSup(sup) {
  if (!sup) {
    return "";
  }

  if (sup === "*") {
    return sup;
  }

  return `<sup>${sup}</sup>`;
}

export default function useLineChartTooltip(xRef, yRef, xBandwidthRef, configRef, dataRef, tooltipRef, marginRef, availableWidthRef, availableHeightRef) {
  const selectedPoint = ref(null);
  const selectedColumn = ref(null);
  const tooltipTop = ref(0);
  const tooltipLeft = ref(0);
  const tooltipData = ref([]);
  const tooltipVisibility = ref("hidden");
  const tooltipTransition = ref(TOOLTIP_NO_ANIMATION_DURATION);
  const tooltipHeaders = ref(null);

  let enableAnimationTimeout;

  const updateTooltipAndColumnPositions = () => {
    if (!dataRef?.value || !selectedPoint?.value || !tooltipRef?.value || !configRef?.value || !xRef?.value || !yRef?.value) {
      return;
    }

    const halfXBandwidth = xBandwidthRef.value / 2;
    const tooltipWidth = tooltipRef.value.offsetWidth;
    const halfTooltipHeight = tooltipRef.value.offsetHeight / 2;

    const maxY = availableHeightRef.value - tooltipRef.value.offsetHeight - TOOLTIP_SPACING;
    const minY = marginRef.value.top;

    let x = xRef.value(selectedPoint.value.x) + halfXBandwidth + TOOLTIP_SPACING;
    let y = Math.max(minY, Math.min(maxY, yRef.value(selectedPoint.value.y) - halfTooltipHeight));

    if (x + tooltipWidth > marginRef.value.left + availableWidthRef.value) {
       // We multiply the spacing by 2 as we need to negate the added space above
       x -= tooltipWidth + (TOOLTIP_SPACING * 2);
    }

    selectedColumn.value = {
      x1: xRef.value(selectedPoint.value.x) + halfXBandwidth,
      y1: yRef.value(configRef.value.yAxis.high),
      x2: xRef.value(selectedPoint.value.x) + halfXBandwidth,
      y2: yRef.value(configRef.value.yAxis.low),
    };

    tooltipLeft.value = x;
    tooltipTop.value = y;
    tooltipVisibility.value = "visible";

    // We want to delay enalbing the transition for the first time
    // the tooltip is displayed, after that all changes are animated
    clearTimeout(enableAnimationTimeout);
    enableAnimationTimeout = setTimeout(() => {
      tooltipTransition.value = TOOLTIP_DEFAULT_ANIMATION_DURATION;
    }, 100);
  };

  const closeTooltip = () => {
    // In the rare case the user dismisses the tooltip before the timeout is completed,
    // the animation would be enabled for a hidden tooltip, and the next time the tooltip was
    // displayed, it would animate towards the new coordinates, which is the undersired behaviour
    clearTimeout(enableAnimationTimeout);

    selectedPoint.value = null;
    selectedColumn.value = null;
    tooltipVisibility.value = "hidden";
    tooltipTransition.value = TOOLTIP_NO_ANIMATION_DURATION;
  };

  const updateTooltipData = () => {
    if (!dataRef?.value || !selectedPoint?.value || !configRef?.value) {
      tooltipData.value = [];
      tooltipHeaders.value = null;
      return;
    }

    // Passing the tooltip headers, if present
    tooltipHeaders.value = configRef.value.tooltipHeaders;

    const newTooltipData = [];

    for (const group of dataRef.value) {
      for (const set of group.sets) {
        for (const point of set.points) {
          // Double equals to leverage implicit
          // conversion between number and string
          if (point.x == selectedPoint.value.x) {
            newTooltipData.push({
              id: `${group.name}-${set.name}`,
              yValue: `${point.y}${getFormattedSup(point?.label?.sup)}`,
              label: set.tooltipLabel,
              color: set.color,
              line: set.line,
              background: set.shape === "dot" ? set.color : "#fff",
              fontWeight: point.y == selectedPoint.value.y ? 500 : 300
            });
          }
        }
      }
    }

    tooltipData.value = newTooltipData;

    requestAnimationFrame(updateTooltipAndColumnPositions);
  };

  watch([dataRef, selectedPoint, xRef, yRef], updateTooltipData);
  watch([marginRef, availableWidthRef, availableHeightRef, xRef, yRef, xBandwidthRef], updateTooltipAndColumnPositions);

  onMounted(updateTooltipAndColumnPositions);

  return {
    tooltipData,
    selectedColumn,
    selectedPoint,
    tooltipTop,
    tooltipLeft,
    tooltipVisibility,
    tooltipTransition,
    tooltipHeaders,
    closeTooltip
  };
}
